import { Link, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import {
  getLibrary,
  getLibraryItems,
  getDiscoveryFacets,
  getAllPlaybackResumeItems,
  getMySettings,
  getWatchStates,
  scanLibrary,
  updateMySettings,
} from "@/api/endpoints";
import { MediaCard } from "@/components/MediaCard";
import { RatingBadge } from "@/components/RatingBadge";
import type { Item, WatchStateResource } from "@/types/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  bestWatchStateCardProgressForItems,
  buildResumeCardProgressMap,
  cardProgressForItem,
  resumeCardProgressForItem,
  type CardWatchProgress,
} from "@/lib/watchState";
import { formatReleaseDate } from "@/lib/dates";
import {
  clearItemPlaybackState,
  markItemPlayed,
} from "@/lib/watchPlayback";
import clsx from "clsx";

type ViewMode = "grid" | "list";
type SortField = "title" | "-title" | "-created_at" | "year" | "-year" | "release_date" | "-release_date" | "rating" | "-rating";
type ItemTypeFilter = "all" | "show" | "episode" | "movie" | "season" | "track" | "photo" | "audiobook" | "book";
type ScanMode = "incremental" | "full";

const FALLBACK_LIBRARY_SORT: SortField = "-release_date";

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: "title", label: "Title A-Z" },
  { value: "-title", label: "Title Z-A" },
  { value: "-created_at", label: "Date Added" },
  { value: "-release_date", label: "Release Date (Newest)" },
  { value: "release_date", label: "Release Date (Oldest)" },
  { value: "-year", label: "Year (Newest)" },
  { value: "year", label: "Year (Oldest)" },
  { value: "-rating", label: "Rating (Highest)" },
  { value: "rating", label: "Rating (Lowest)" },
];

const ALPHABET_INDEX = ["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];

export function LibraryPage() {
  const { libraryId } = useParams<{ libraryId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [query, setQuery] = useState("");
  const [scanning, setScanning] = useState<ScanMode | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [itemsError, setItemsError] = useState<Error | null>(null);
  const [watchError, setWatchError] = useState<string | null>(null);
  const [watchUpdatingId, setWatchUpdatingId] = useState<string | null>(null);
  const [watchOverrides, setWatchOverrides] = useState<Record<string, boolean>>({});
  const [watchStateByItem, setWatchStateByItem] = useState<Map<string, WatchStateResource>>(() => new Map());
  const [savingDefaultSort, setSavingDefaultSort] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const requestKeyRef = useRef<string | null>(null);
  const itemAnchorRefs = useRef(new Map<string, HTMLDivElement | null>());
  const fetchedWatchStateItemIdsRef = useRef(new Set<string>());
  const pendingWatchStateItemIdsRef = useRef(new Set<string>());
  const latestWatchStateRequestByItemRef = useRef(new Map<string, number>());
  const watchStateRequestSequenceRef = useRef(0);

  const library = useApi(() => getLibrary(libraryId!), [libraryId]);
  const discoveryFacets = useApi(
    () => getDiscoveryFacets({ "filter[library_id]": libraryId! }),
    [libraryId],
  );
  const mySettings = useApi(() => getMySettings(), []);
  const resumeItems = useApi(() => getAllPlaybackResumeItems(), []);
  const watchItemIds = useMemo(() => uniqueItemIds(items), [items]);
  const resumeProgressByKey = useMemo(
    () => buildResumeCardProgressMap(resumeItems.data?.data ?? []),
    [resumeItems.data],
  );
  const rawSort = searchParams.get("sort");
  const rawItemType = searchParams.get("item_type");
  const rawGenre = searchParams.get("genre");
  const preferredSort = parsePreferredLibrarySort(mySettings.data?.data.attributes.library_default_sort);
  const sort: SortField = isSortField(rawSort) ? rawSort : preferredSort;
  const itemTypeFilter: ItemTypeFilter = isItemTypeFilter(rawItemType) ? rawItemType : "all";
  const genreFilter = rawGenre?.trim() ?? "";
  const genreFacetValues = useMemo(
    () => discoveryFacets.data?.data.find((facet) => facet.id === "genres")?.attributes.values ?? [],
    [discoveryFacets.data],
  );
  const selectedGenre = useMemo(
    () => genreFacetValues.find((genre) => genre.id === genreFilter),
    [genreFacetValues, genreFilter],
  );

  const fetchWatchStatesForItemIds = useCallback(async (
    itemIds: string[],
    options: { force?: boolean; clearOverrides?: boolean } = {},
  ) => {
    const force = options.force === true;
    const ids = uniqueStringIds(itemIds).filter((itemId) => {
      if (force) return true;
      return !fetchedWatchStateItemIdsRef.current.has(itemId)
        && !pendingWatchStateItemIdsRef.current.has(itemId);
    });
    if (ids.length === 0) {
      return;
    }

    const requestSequence = ++watchStateRequestSequenceRef.current;
    for (const itemId of ids) {
      fetchedWatchStateItemIdsRef.current.add(itemId);
      pendingWatchStateItemIdsRef.current.add(itemId);
      latestWatchStateRequestByItemRef.current.set(itemId, requestSequence);
    }

    try {
      const response = await getWatchStates(ids);
      setWatchStateByItem((current) => {
        let next: Map<string, WatchStateResource> | null = null;
        for (const resource of response.data) {
          const itemId = resource.attributes.item_id;
          if (latestWatchStateRequestByItemRef.current.get(itemId) !== requestSequence) {
            continue;
          }
          if (!next) {
            next = new Map(current);
          }
          next.set(itemId, resource);
        }
        return next ?? current;
      });

      if (options.clearOverrides) {
        setWatchOverrides((current) => removeWatchOverrides(current, ids));
      }
    } finally {
      for (const itemId of ids) {
        pendingWatchStateItemIdsRef.current.delete(itemId);
      }
    }
  }, []);

  useEffect(() => {
    void fetchWatchStatesForItemIds(watchItemIds).catch(() => {});
  }, [fetchWatchStatesForItemIds, watchItemIds]);

  const loadItems = useCallback(async (cursor: string | null, reset: boolean) => {
    if (!libraryId) return;

    const requestKey = `${reset ? "reset" : "page"}:${cursor ?? "start"}:${sort}:${itemTypeFilter}:${genreFilter}`;
    if (requestKeyRef.current === requestKey) return;
    requestKeyRef.current = requestKey;

    if (reset) {
      setItemsLoading(true);
    } else {
      setLoadingMore(true);
    }
    setItemsError(null);

    try {
      const response = await getLibraryItems(libraryId, {
        limit: 60,
        sort,
        "filter[item_type]": itemTypeFilter === "all" ? undefined : itemTypeFilter,
        "filter[genre]": genreFilter || undefined,
        cursor: cursor ?? undefined,
      });

      setItems((current) => (reset ? response.data : [...current, ...response.data]));
      setNextCursor(response.page.next_cursor);
    } catch (error) {
      setItemsError(error instanceof Error ? error : new Error("Failed to load items"));
      if (reset) {
        setItems([]);
        setNextCursor(null);
      }
    } finally {
      requestKeyRef.current = null;
      setItemsLoading(false);
      setLoadingMore(false);
    }
  }, [genreFilter, itemTypeFilter, libraryId, sort]);

  useEffect(() => {
    setItems([]);
    setNextCursor(null);
    void loadItems(null, true);
  }, [loadItems]);

  useEffect(() => {
    if (!nextCursor || itemsLoading || loadingMore || !loadMoreRef.current) {
      return;
    }

    const target = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadItems(nextCursor, false);
        }
      },
      { rootMargin: "600px 0px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [itemsLoading, loadItems, loadingMore, nextCursor]);

  async function handleScan(mode: ScanMode) {
    if (!libraryId) return;
    setScanning(mode);
    try {
      await scanLibrary(libraryId, { mode });
    } finally {
      setScanning(null);
    }
  }

  async function handleSaveDefaultSort() {
    setSavingDefaultSort(true);
    setWatchError(null);
    try {
      await updateMySettings({ library_default_sort: sort });
      await mySettings.refetch();
    } catch {
      setWatchError("Failed to save your default library sort.");
    } finally {
      setSavingDefaultSort(false);
    }
  }

  if (library.loading) return <div className="page-loading">Loading...</div>;
  if (library.error) return <p className="error-message">Library not found</p>;
  if (!library.data) return null;

  const lib = library.data.data;
  const isSeriesLibrary = lib.attributes.library_type === "tv";
  const libraryItemCount = lib.attributes.item_count;
  const displayItems = buildLibraryDisplayItems(items, isSeriesLibrary, itemTypeFilter, sort);
  const normalizedQuery = query.trim().toLowerCase();
  const showAlphabetRail = normalizedQuery.length === 0 && (sort === "title" || sort === "-title");
  const alphabetIndex = sort === "-title" ? [...ALPHABET_INDEX].reverse() : ALPHABET_INDEX;
  const visibleItems = normalizedQuery
    ? displayItems.filter((item) => {
        const title = item.attributes.title.toLowerCase();
        const year = item.attributes.year ? String(item.attributes.year) : "";
        return title.includes(normalizedQuery) || year.includes(normalizedQuery);
      })
    : displayItems;
  const firstItemIdByLetter = new Map<string, string>();
  for (const item of visibleItems) {
    const letter = alphabetKeyForItem(item);
    if (!firstItemIdByLetter.has(letter)) {
      firstItemIdByLetter.set(letter, item.id);
    }
  }

  async function handleToggleWatched(item: Item) {
    const targetItems = getLibraryWatchScopeItems(item, items);
    if (targetItems.length === 0) {
      setWatchError("This card does not have enough episode data to update watched state.");
      return;
    }
    if (targetItems.some((targetItem) => (targetItem.attributes.duration_ms ?? 0) <= 0)) {
      setWatchError("This card does not have complete playback metadata yet.");
      return;
    }

    setWatchUpdatingId(item.id);
    setWatchError(null);

    try {
      const fullyWatched = targetItems.every((targetItem) =>
        isItemWatched(targetItem, watchStateByItem, watchOverrides));

      if (fullyWatched) {
        await clearItemPlaybackState(item.id);
        setWatchOverrides((current) => applyWatchOverrides(current, targetItems, false));
      } else {
        await markItemPlayed(item.id);
        setWatchOverrides((current) => applyWatchOverrides(current, targetItems, true));
      }

      void fetchWatchStatesForItemIds(uniqueItemIds(targetItems), {
        force: true,
        clearOverrides: true,
      }).catch(() => {});
    } catch {
      setWatchError("Failed to update watched state.");
    } finally {
      setWatchUpdatingId(null);
    }
  }

  function setItemAnchorRef(itemId: string, element: HTMLDivElement | null) {
    if (element) {
      itemAnchorRefs.current.set(itemId, element);
    } else {
      itemAnchorRefs.current.delete(itemId);
    }
  }

  function scrollToItem(itemId: string) {
    const target = itemAnchorRefs.current.get(itemId);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function handleJumpToLetter(letter: string) {
    const itemId = firstItemIdByLetter.get(letter);
    if (itemId) {
      scrollToItem(itemId);
      return;
    }

    if (!libraryId || !nextCursor || itemsLoading || loadingMore) return;

    setLoadingMore(true);
    setItemsError(null);

    let cursor: string | null = nextCursor;
    let accumulatedItems = items;

    try {
      let targetItemId: string | null = null;
      while (cursor) {
        const response = await getLibraryItems(libraryId, {
          limit: 60,
          sort,
          "filter[item_type]": itemTypeFilter === "all" ? undefined : itemTypeFilter,
          "filter[genre]": genreFilter || undefined,
          cursor,
        });

        accumulatedItems = [...accumulatedItems, ...response.data];
        cursor = response.page.next_cursor;

        const candidate = buildLibraryDisplayItems(accumulatedItems, isSeriesLibrary, itemTypeFilter, sort)
          .find((candidateItem) => alphabetKeyForItem(candidateItem) === letter);
        if (candidate) {
          targetItemId = candidate.id;
          break;
        }
      }

      setItems(accumulatedItems);
      setNextCursor(cursor);
      if (targetItemId) {
        const target = targetItemId;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => scrollToItem(target));
        });
      }
    } catch (error) {
      setItemsError(error instanceof Error ? error : new Error("Failed to load items"));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="library-page">
      {/* Page header */}
      <div className="page-header">
        <h1>
          {lib.attributes.name}
          {typeof libraryItemCount === "number" && ` (${libraryItemCount.toLocaleString()})`}
        </h1>
        <div className="page-header-actions-block">
          <div className="page-header-actions">
            <button
              onClick={() => void handleScan("incremental")}
              disabled={scanning !== null}
              className="btn btn--secondary"
            >
              {scanning === "incremental" ? "Quick Scanning..." : "Quick Scan"}
            </button>
            <button
              onClick={() => void handleScan("full")}
              disabled={scanning !== null}
              className="btn btn--secondary"
            >
              {scanning === "full" ? "Full Rescanning..." : "Full Rescan"}
            </button>
          </div>
          <p className="page-header-help">
            Quick Scan finds new files and fills in missing metadata. Full Rescan rebuilds the whole library.
          </p>
          {watchError && <p className="error-message">{watchError}</p>}
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <input
            type="search"
            className="toolbar-search-input"
            placeholder={`Search ${lib.attributes.name.toLowerCase()}...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            className="toolbar-select"
            value={sort}
            onChange={(e) => updateLibraryViewParams(setSearchParams, searchParams, {
              sort: e.target.value as SortField,
            })}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--secondary library-default-sort-btn"
            onClick={() => void handleSaveDefaultSort()}
            disabled={savingDefaultSort || sort === preferredSort}
            title={sort === preferredSort ? "This is already your default sort" : "Make the current sort your default"}
          >
            {savingDefaultSort ? "Saving..." : sort === preferredSort ? "Default Sort" : "Make Default"}
          </button>
        </div>
        <div className="toolbar-right">
          <button
            className={clsx("toolbar-view-btn", { active: viewMode === "grid" })}
            onClick={() => setViewMode("grid")}
            title="Grid view"
          >
            ⊞
          </button>
          <button
            className={clsx("toolbar-view-btn", { active: viewMode === "list" })}
            onClick={() => setViewMode("list")}
            title="List view"
          >
            ☰
          </button>
          <span className="toolbar-count">
            {visibleItems.length > 0
              ? `${visibleItems.length}${visibleItems.length !== displayItems.length ? ` of ${displayItems.length}` : ""} loaded${nextCursor ? "+" : ""}`
              : ""}
          </span>
        </div>
      </div>

      {(genreFacetValues.length > 0 || genreFilter) && (
        <section className="library-facet-panel" aria-label="Genres">
          <h2>Genres</h2>
          <div className="library-facet-grid">
            <button
              type="button"
              className={clsx("library-facet-button", { active: genreFilter === "" })}
              aria-pressed={genreFilter === ""}
              onClick={() => updateLibraryViewParams(setSearchParams, searchParams, { genre: null })}
            >
              <span>All Genres</span>
            </button>
            {genreFilter && !selectedGenre && (
              <button
                type="button"
                className="library-facet-button active"
                aria-pressed="true"
                onClick={() => updateLibraryViewParams(setSearchParams, searchParams, { genre: null })}
              >
                <span>{genreLabelFromID(genreFilter)}</span>
              </button>
            )}
            {genreFacetValues.map((genre) => (
              <button
                key={genre.id}
                type="button"
                className={clsx("library-facet-button", { active: genre.id === genreFilter })}
                aria-pressed={genre.id === genreFilter}
                onClick={() => updateLibraryViewParams(setSearchParams, searchParams, {
                  genre: genre.id === genreFilter ? null : genre.id,
                })}
              >
                <span>{genre.label}</span>
                <span>{genre.count.toLocaleString()}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Content */}
      {itemsLoading && <div className="page-loading">Loading items...</div>}
      {itemsError && <p className="error-message">{itemsError.message}</p>}
      {!itemsLoading && !itemsError && visibleItems.length === 0 && (
        <div className="library-empty-state">No items matched this library view.</div>
      )}
      {visibleItems.length > 0 && (
        <div className={`library-browser${showAlphabetRail ? "" : " library-browser--no-alpha"}`}>
          {showAlphabetRail && (
            <aside className="library-alpha-nav" aria-label="Jump to letter">
              {alphabetIndex.map((letter) => {
                const enabled = firstItemIdByLetter.has(letter) || nextCursor !== null;
                return (
                  <button
                    key={letter}
                    type="button"
                    className={`library-alpha-link${enabled ? "" : " library-alpha-link--disabled"}`}
                    disabled={!enabled}
                    onClick={() => void handleJumpToLetter(letter)}
                  >
                    {letter}
                  </button>
                );
              })}
            </aside>
          )}

          {viewMode === "grid" ? (
            <div className="item-grid">
              {visibleItems.map((item) => {
                const watchScopeItems = getLibraryWatchScopeItems(item, items);
                const canToggleWatched = watchScopeItems.length > 0
                  && watchScopeItems.every((targetItem) => (targetItem.attributes.duration_ms ?? 0) > 0);
                const cardProgress = libraryCardProgress(item, items, watchStateByItem, resumeProgressByKey);

                return (
                  <div
                    key={item.id}
                    ref={(element) => setItemAnchorRef(item.id, element)}
                    className="library-anchor-item"
                  >
                    <MediaCard
                      item={item}
                      progress={cardProgress?.progress}
                      progressLabel={cardProgress?.label}
                      progressTitle={cardProgress?.title}
                      watched={isLibraryCardWatched(item, items, watchStateByItem, watchOverrides)}
                      watchUpdating={watchUpdatingId === item.id}
                      onToggleWatched={canToggleWatched ? () => void handleToggleWatched(item) : undefined}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="item-list">
              {visibleItems.map((item) => (
                <div
                  key={item.id}
                  ref={(element) => setItemAnchorRef(item.id, element)}
                  className="library-anchor-item"
                >
                  <ItemListRow
                    item={item}
                    progress={libraryCardProgress(item, items, watchStateByItem, resumeProgressByKey)?.progress}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!itemsLoading && !itemsError && (
        <div className="library-load-state">
          {loadingMore && <span>Loading more...</span>}
          {!loadingMore && nextCursor && (
            <button
              className="btn btn--secondary"
              onClick={() => void loadItems(nextCursor, false)}
            >
              Load more
            </button>
          )}
          {!loadingMore && !nextCursor && visibleItems.length > 0 && (
            <span>End of library</span>
          )}
          <div ref={loadMoreRef} className="library-load-sentinel" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

function ItemListRow({ item, progress }: { item: Item; progress?: number }) {
  const { attributes: attrs } = item;
  const secondaryMeta = formatReleaseDate(attrs.release_date) ?? (attrs.year ? String(attrs.year) : null);
  const query = attrs.item_type === "show" && attrs.series_title && attrs.series_id && attrs.series_id !== item.id
    ? "?context=series"
    : "";
  return (
    <Link to={`/items/${item.id}${query}`} className="item-list-row">
      <span className="item-list-title">{attrs.title}</span>
      <span className="item-list-year">{secondaryMeta}</span>
      <span className="item-list-rating">
        <RatingBadge item={item} placement="detail" />
      </span>
      {progress !== undefined && progress > 0 && (
        <span className="item-list-year">{Math.round(progress)}%</span>
      )}
      <span className="item-list-type badge">{attrs.item_type === "show" ? "series" : attrs.item_type}</span>
    </Link>
  );
}

function isSortField(value: string | null): value is SortField {
  return value === "title"
    || value === "-title"
    || value === "-created_at"
    || value === "year"
    || value === "-year"
    || value === "release_date"
    || value === "-release_date"
    || value === "rating"
    || value === "-rating";
}

// Unrated items sort last in BOTH rating directions: a missing rating is
// absence of information, not a score of zero. Mirrors the server's order.
function compareRating(a: Item, b: Item, desc: boolean): number {
  const ra = a.attributes.rating_average ?? 0;
  const rb = b.attributes.rating_average ?? 0;
  if ((ra > 0) !== (rb > 0)) return ra > 0 ? -1 : 1;
  if (ra > 0 && ra !== rb) return desc ? rb - ra : ra - rb;
  return compareNumbersDesc(a.attributes.rating_votes ?? 0, b.attributes.rating_votes ?? 0);
}

function parsePreferredLibrarySort(value: string | null | undefined): SortField {
  return value != null && isSortField(value) ? value : FALLBACK_LIBRARY_SORT;
}

function isItemTypeFilter(value: string | null): value is ItemTypeFilter {
  return value === "all"
    || value === "show"
    || value === "episode"
    || value === "movie"
    || value === "season"
    || value === "track"
    || value === "photo"
    || value === "audiobook"
    || value === "book";
}

function updateLibraryViewParams(
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  current: URLSearchParams,
  updates: { sort?: SortField; itemType?: ItemTypeFilter; genre?: string | null },
) {
  const next = new URLSearchParams(current);
  if (updates.sort) {
    next.set("sort", updates.sort);
  }
  if (updates.itemType) {
    if (updates.itemType === "all") {
      next.delete("item_type");
    } else {
      next.set("item_type", updates.itemType);
    }
  }
  if (updates.genre !== undefined) {
    const genre = updates.genre?.trim() ?? "";
    if (genre === "") {
      next.delete("genre");
    } else {
      next.set("genre", genre);
    }
  }
  setSearchParams(next, { replace: true });
}

function genreLabelFromID(value: string): string {
  return value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function normalizeComparable(value: string | null | undefined): string | null {
  return value ? value.trim().toLowerCase() : null;
}

function getLibraryWatchScopeItems(item: Item, loadedItems: Item[]): Item[] {
  switch (item.attributes.item_type) {
    case "movie":
    case "episode":
      return [item];
    case "show": {
      const targetSeriesId = item.attributes.series_id ?? null;
      const targetSeriesTitle = normalizeComparable(item.attributes.series_title ?? item.attributes.title);
      return loadedItems.filter((candidate) => {
        if (candidate.attributes.item_type !== "episode") return false;
        if (targetSeriesId && candidate.attributes.series_id) {
          return targetSeriesId === candidate.attributes.series_id;
        }
        return targetSeriesTitle !== null
          && targetSeriesTitle === normalizeComparable(candidate.attributes.series_title);
      });
    }
    case "season": {
      const targetSeasonId = item.attributes.season_id ?? item.id;
      const targetSeasonNumber = item.attributes.season_number ?? null;
      const targetSeriesId = item.attributes.series_id ?? null;
      return loadedItems.filter((candidate) => {
        if (candidate.attributes.item_type !== "episode") return false;
        if (candidate.attributes.season_id && candidate.attributes.season_id === targetSeasonId) {
          return true;
        }
        return targetSeasonNumber != null
          && candidate.attributes.season_number === targetSeasonNumber
          && candidate.attributes.series_id === targetSeriesId;
      });
    }
    default:
      return [];
  }
}

function applyWatchOverrides(current: Record<string, boolean>, items: Item[], watched: boolean): Record<string, boolean> {
  const next = { ...current };
  for (const item of items) {
    next[item.id] = watched;
  }
  return next;
}

function removeWatchOverrides(current: Record<string, boolean>, itemIds: string[]): Record<string, boolean> {
  let next: Record<string, boolean> | null = null;
  for (const itemId of itemIds) {
    if (!(itemId in current)) {
      continue;
    }
    if (!next) {
      next = { ...current };
    }
    delete next[itemId];
  }
  return next ?? current;
}

function isItemWatched(
  item: Item,
  watchStateByItem: Map<string, WatchStateResource>,
  watchOverrides: Record<string, boolean>,
): boolean {
  if (item.id in watchOverrides) {
    return watchOverrides[item.id]!;
  }
  return watchStateByItem.get(item.id)?.attributes.watched === true;
}

function isLibraryCardWatched(
  item: Item,
  loadedItems: Item[],
  watchStateByItem: Map<string, WatchStateResource>,
  watchOverrides: Record<string, boolean>,
): boolean {
  const targetItems = getLibraryWatchScopeItems(item, loadedItems);
  return targetItems.length > 0
    && targetItems.every((targetItem) =>
      isItemWatched(targetItem, watchStateByItem, watchOverrides));
}

function libraryCardProgress(
  item: Item,
  loadedItems: Item[],
  watchStateByItem: Map<string, WatchStateResource>,
  resumeProgressByKey: Map<string, CardWatchProgress>,
): CardWatchProgress | undefined {
  if (item.attributes.item_type === "show" || item.attributes.item_type === "season") {
    return resumeCardProgressForItem(item, resumeProgressByKey)
      ?? bestWatchStateCardProgressForItems(getLibraryWatchScopeItems(item, loadedItems), watchStateByItem)
      ?? cardProgressForItem(item, watchStateByItem, resumeProgressByKey);
  }
  return cardProgressForItem(item, watchStateByItem, resumeProgressByKey);
}

function uniqueItemIds(items: Item[]): string[] {
  return uniqueStringIds(items.map((item) => item.id));
}

function uniqueStringIds(itemIds: string[]): string[] {
  return Array.from(new Set(itemIds.map((itemId) => itemId.trim()).filter(Boolean)));
}

function toSeriesLibraryItems(items: Item[], sort: SortField): Item[] {
  const grouped = new Map<string, Item>();

  for (const item of items) {
    const seriesTitle = item.attributes.series_title?.trim();
    if (!seriesTitle) continue;

    const key = (item.attributes.series_id?.trim() || seriesTitle).toLowerCase();
    const current = grouped.get(key);
    if (!current || compareSeriesRepresentative(item, current, sort) < 0) {
      grouped.set(key, item);
    }
  }

  return [...grouped.values()]
    .map((item) => ({
      ...item,
      attributes: {
        ...item.attributes,
        item_type: "show" as const,
        title: item.attributes.series_title?.trim() || item.attributes.title,
        episode_title: null,
      },
    }))
    .sort((a, b) => compareDisplayItems(a, b, sort));
}

function buildLibraryDisplayItems(
  items: Item[],
  isSeriesLibrary: boolean,
  itemTypeFilter: ItemTypeFilter,
  sort: SortField,
): Item[] {
  const hasRealSeriesItems = isSeriesLibrary && items.some((item) => item.attributes.item_type === "show");
  return isSeriesLibrary && itemTypeFilter === "all" && !hasRealSeriesItems
    ? toSeriesLibraryItems(items, sort)
    : sortLibraryItems(items, sort);
}

function sortLibraryItems(items: Item[], sort: SortField): Item[] {
  return [...items].sort((a, b) => compareDisplayItems(a, b, sort));
}

function compareSeriesRepresentative(a: Item, b: Item, sort: SortField): number {
  switch (sort) {
    case "-created_at":
      return compareNumbersDesc(toTimestamp(a.attributes.created_at), toTimestamp(b.attributes.created_at))
        || compareNumbersAsc(a.attributes.season_number ?? 0, b.attributes.season_number ?? 0)
        || compareNumbersAsc(a.attributes.episode_number ?? 0, b.attributes.episode_number ?? 0);
    case "-release_date":
      return compareNumbersDesc(toTimestamp(a.attributes.release_date ?? ""), toTimestamp(b.attributes.release_date ?? ""))
        || compareNumbersAsc(a.attributes.season_number ?? 0, b.attributes.season_number ?? 0)
        || compareNumbersAsc(a.attributes.episode_number ?? 0, b.attributes.episode_number ?? 0);
    case "release_date":
      return compareNumbersAsc(
        toTimestamp(a.attributes.release_date ?? "") || Number.MAX_SAFE_INTEGER,
        toTimestamp(b.attributes.release_date ?? "") || Number.MAX_SAFE_INTEGER,
      )
        || compareNumbersAsc(a.attributes.season_number ?? 0, b.attributes.season_number ?? 0)
        || compareNumbersAsc(a.attributes.episode_number ?? 0, b.attributes.episode_number ?? 0);
    case "-year":
      return compareNumbersDesc(a.attributes.year ?? 0, b.attributes.year ?? 0)
        || compareNumbersAsc(a.attributes.season_number ?? 0, b.attributes.season_number ?? 0)
        || compareNumbersAsc(a.attributes.episode_number ?? 0, b.attributes.episode_number ?? 0);
    case "year":
      return compareNumbersAsc(a.attributes.year ?? Number.MAX_SAFE_INTEGER, b.attributes.year ?? Number.MAX_SAFE_INTEGER)
        || compareNumbersAsc(a.attributes.season_number ?? 0, b.attributes.season_number ?? 0)
        || compareNumbersAsc(a.attributes.episode_number ?? 0, b.attributes.episode_number ?? 0);
    case "-rating":
    case "rating":
      return compareRating(a, b, sort === "-rating")
        || compareNumbersAsc(a.attributes.season_number ?? 0, b.attributes.season_number ?? 0)
        || compareNumbersAsc(a.attributes.episode_number ?? 0, b.attributes.episode_number ?? 0);
    default:
      return compareNumbersAsc(a.attributes.season_number ?? 0, b.attributes.season_number ?? 0)
        || compareNumbersAsc(a.attributes.episode_number ?? 0, b.attributes.episode_number ?? 0);
  }
}

function compareDisplayItems(a: Item, b: Item, sort: SortField): number {
  switch (sort) {
    case "title":
      return compareTextAsc(displayTitle(a), displayTitle(b))
        || compareNumbersAsc(a.attributes.year ?? Number.MAX_SAFE_INTEGER, b.attributes.year ?? Number.MAX_SAFE_INTEGER);
    case "-title":
      return compareTextDesc(displayTitle(a), displayTitle(b))
        || compareNumbersDesc(a.attributes.year ?? 0, b.attributes.year ?? 0);
    case "year":
      return compareNumbersAsc(a.attributes.year ?? Number.MAX_SAFE_INTEGER, b.attributes.year ?? Number.MAX_SAFE_INTEGER)
        || compareTextAsc(displayTitle(a), displayTitle(b));
    case "-year":
      return compareNumbersDesc(a.attributes.year ?? 0, b.attributes.year ?? 0)
        || compareTextAsc(displayTitle(a), displayTitle(b));
    case "-release_date":
      return compareNumbersDesc(toTimestamp(a.attributes.release_date ?? ""), toTimestamp(b.attributes.release_date ?? ""))
        || compareTextAsc(displayTitle(a), displayTitle(b));
    case "release_date":
      return compareNumbersAsc(
        toTimestamp(a.attributes.release_date ?? "") || Number.MAX_SAFE_INTEGER,
        toTimestamp(b.attributes.release_date ?? "") || Number.MAX_SAFE_INTEGER,
      )
        || compareTextAsc(displayTitle(a), displayTitle(b));
    case "-rating":
    case "rating":
      return compareRating(a, b, sort === "-rating")
        || compareTextAsc(displayTitle(a), displayTitle(b));
    case "-created_at":
    default:
      return compareNumbersDesc(toTimestamp(a.attributes.created_at), toTimestamp(b.attributes.created_at))
        || compareTextAsc(displayTitle(a), displayTitle(b));
  }
}

function displayTitle(item: Item): string {
  return (item.attributes.series_title || item.attributes.title).trim().toLocaleLowerCase();
}

function toTimestamp(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function compareTextAsc(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });
}

function compareTextDesc(a: string, b: string): number {
  return compareTextAsc(b, a);
}

function compareNumbersAsc(a: number, b: number): number {
  return a - b;
}

function compareNumbersDesc(a: number, b: number): number {
  return b - a;
}

function alphabetKeyForItem(item: Item): string {
  const title = (item.attributes.series_title || item.attributes.title).trim();
  const firstChar = title.charAt(0).toUpperCase();
  return firstChar >= "A" && firstChar <= "Z" ? firstChar : "#";
}
